<script setup>
import ImportBacklinksAction from "@frontJs/components/actions/projectManagement/seo/ImportBacklinksAction.vue";
import BacklinkActionsCell from "@frontJs/components/tables/cells/projectManagement/seo/BacklinkActionsCell.vue";
import { PlusIcon } from "@heroicons/vue/24/solid";
import { ArrowUpTrayIcon } from "@heroicons/vue/24/solid";
import ActionButton from "@js/components/ActionButton.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import ExportButton from "@js/components/buttons/ExportButton.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import StatusCellV2 from "@js/components/tables/cells/StatusCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatDate } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";

const props = defineProps({
  tenant: {
    type: Object,
    required: true,
  },
  websiteVariants: {
    type: Array,
    required: true,
  },
  baseRequest: {
    type: Object,
    required: false,
    default: () => [],
  },
});

const { t } = useI18n();

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  data,
  count,
  pagination,
  pageCount,
  sorting,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
  selectionIds,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "websiteVariant",
      header: () => t("label.websiteVariant"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.websiteVariant,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "sourceUrl",
      header: () => t("label.sourceUrl"),
      cell: (props) =>
        h(LinkCellV2, {
          text: new URL(props.row.original.sourceUrl).hostname,
          href: props.row.original.sourceUrl,
          maxWidth: 250,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "date",
      header: () => t("label.date"),
      cell: (props) =>
        props.row.original.date
          ? h(TextCellV2, {
              text: formatDate(
                props.row.original.date,
                false,
                false,
                false,
                true,
                true,
              ),
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "active",
      header: () => t("label.active"),
      cell: (props) =>
        props.row.original.active !== null
          ? h(StatusCellV2, {
              status: props.row.original.active,
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "targetUrl",
      header: () => t("label.targetUrl"),
      cell: (props) =>
        props.row.original.targetUrl !== null
          ? h(LinkCellV2, {
              text: props.row.original.targetUrl,
              href: props.row.original.targetUrl,
              maxWidth: 250,
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "sourceAnchor",
      header: () => t("label.sourceAnchor"),
      cell: (props) =>
        props.row.original.sourceAnchor !== null
          ? h(TextCellV2, {
              text: props.row.original.sourceAnchor,
              title: props.row.original.sourceAnchor,
              maxWidth: 250,
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "noFollow",
      header: () => t("label.noFollow"),
      cell: (props) =>
        props.row.original.noFollow !== null
          ? h(StatusCellV2, {
              status: !props.row.original.noFollow,
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "indirectLink",
      header: () => t("label.indirect"),
      cell: (props) =>
        props.row.original.indirectLink !== null
          ? h(StatusCellV2, {
              status: props.row.original.indirectLink,
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "checkedAt",
      header: () => t("label.checkedAt"),
      cell: (props) =>
        props.row.original.checkedAt !== null
          ? h(TextCellV2, {
              text: formatDate(props.row.original.checkedAt, false, false),
            })
          : h(CrossIconCell),
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (innerProps) =>
        h(BacklinkActionsCell, {
          tenant: props.tenant,
          row: innerProps.row,
        }),
    }),
  ],
  updateData,
  baseRequest: props.baseRequest,
  selectionIdKey: "id",
  columnPinning: {
    right: ['actions']
  }
});

sorting.value = [
  {
    id: "date",
    desc: true,
  },
];

function exportCsv() {
  loading.value = true;

  let url = route("front.projectManagement.seo.postExportBacklinks", {
    tenant: props.tenant.id,
  });

  let postData = {
    selectionIds: selectionIds.value,
    sorting: sorting.value,
    globalFilter: globalFilter.value,
    filters: filters.value,
  };

  let config = {};

  axios
    .post(url, postData, config)
    .then((response) => {
      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition != null) {
        let filename = contentDisposition
          .split(";")[1]
          .split("=")[1]
          .replace('"', "")
          .replace('"', "");
        filename = decodeURI(filename);

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute("download", filename);

        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(url);
        link.remove();
      }
    })
    .catch((error) => {
      console.log(error);
    })
    .finally(() => {
      loading.value = false;
    });
}

function updateData() {
  loading.value = true;

  let url = route("front.projectManagement.seo.postBacklinks", {
    tenant: props.tenant.id,
  });

  let postData = {
    pageIndex: pagination.value.pageIndex,
    pageSize: pagination.value.pageSize,
    sorting: sorting.value,
    globalFilter: globalFilter.value,
    filters: filters.value,
  };

  let config = {};

  axios
    .post(url, postData, config)
    .then((response) => {
      data.value = response.data.data;
      count.value = response.data.count;
      pagination.value.pageIndex = response.data.pageIndex;
      pagination.value.pageSize = response.data.pageSize;
      pageCount.value = response.data.pageCount;
    })
    .catch((error) => {
      console.log(error);
    })
    .finally(() => {
      loading.value = false;
    });
}

function importSeoBacklinksCallback() {
  toast.success(t("toast.successfullyAdded"), { position: "top-right" });
  updateData();
}
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <div v-else>
    <DataTableLayout
      :table="table"
      :count="count"
      :global-filter="globalFilter"
      :set-global-filter="setGlobalFilter"
      :available-filters="availableFilters"
      :filters="filters"
      :set-filters="setFilters"
    >
      <template #actions>
        <ExportButton
          :csv-export-callback="exportCsv"
          :disabled="selectionIds.length === 0"
        />
        <ImportBacklinksAction
          :tenant="props.tenant"
          :website-variants="props.websiteVariants"
          :callback="importSeoBacklinksCallback"
        >
          <template #button="{ setIsOpen }">
            <ActionButton
              size="small"
              :callback="
                () => {
                  setIsOpen(true);
                }
              "
              :left-icon="ArrowUpTrayIcon"
            >
              {{ t("button.import") }}
            </ActionButton>
          </template>
        </ImportBacklinksAction>

        <ActionButton
          size="small"
          :href="
            route('front.projectManagement.seo.createBacklink', {
              tenant: props.tenant,
            })
          "
          :left-icon="PlusIcon"
        >
          {{ t("all.button.add") }}
        </ActionButton>
      </template>
    </DataTableLayout>
  </div>
</template>
